import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  fetchMinhasVisitas,
  type CatalogoOficinaExterna,
  type MinhaVisita,
} from '../../../api/oficinaExternaApi';
import type { ItemDaFila } from '../../../infrastructure/offline/oficinaExternaDb';
import { notify } from '../../../shared/feedback/notify';
import Paginacao from '../../../components/paginacao/Paginacao';
import { dataCurta, dataHora, plural, valorLegivel } from '../formatacao';
import FiltroMinhasVisitasModal, { type FiltrosDeMinhasVisitas } from './FiltroMinhasVisitasModal';
import '../../admin-sistema/pages/css/adminSistema.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/badge-status.css';
import '../pages/OficinaExterna.css';

const POR_PAGINA = 20;

/** Uma visita na lista: veio do servidor, ou ainda está na fila deste aparelho. */
type VisitaNaLista = MinhaVisita & { noAparelho?: boolean; recusada?: boolean };

interface Props {
  /** A fila local, para as visitas que ainda não subiram aparecerem aqui também. */
  fila: ItemDaFila[];
  /** Resolve nome de etapa e rótulo de pergunta das visitas que ainda estão no aparelho. */
  catalogo: CatalogoOficinaExterna | null;
  onAbrirVeiculo: (atendimentoId: string, placa: string) => void;
  sidebarRef?: React.RefObject<HTMLElement>;
}

/**
 * Converte um item `VISITA` da fila no mesmo formato da lista do servidor.
 *
 * 🔴 Sem isto, a visita registrada sem rede não apareceria em "Minhas visitas" — e o inspetor,
 * que acabou de gravá-la, leria a ausência como trabalho perdido. É a mesma razão pela qual a
 * árvore já mostra os cadastros locais.
 */
const daFila = (item: ItemDaFila, catalogo: CatalogoOficinaExterna | null): VisitaNaLista => {
  const payload = (item.payload ?? {}) as {
    dataVisita?: string;
    etapaId?: string;
    previsaoEntrega?: string;
    previsaoEtapa?: string;
    observacao?: string;
    respostas?: Array<{ itemId: string; valor: unknown }>;
  };

  const etapa = catalogo?.etapas.find((e) => e.id === payload.etapaId);
  const itensDoChecklist = catalogo?.checklist.itens ?? [];

  return {
    id: item.clienteId,
    dataVisita: payload.dataVisita ?? item.criadoEm,
    registradaEm: item.criadoEm,
    previsaoEntrega: payload.previsaoEntrega ?? null,
    previsaoEtapa: payload.previsaoEtapa ?? null,
    observacao: payload.observacao ?? null,
    etapa: etapa ? { id: etapa.id, nome: etapa.nome } : null,
    // As fotos da visita são itens PRÓPRIOS da fila, ligados por `visitaRef`.
    totalFotos: 0,
    respostas: (payload.respostas ?? []).map((r) => {
      const definicao = itensDoChecklist.find((i) => i.id === r.itemId);
      return {
        itemId: r.itemId,
        valor: r.valor,
        rotulo: definicao?.rotulo,
        tipo: definicao?.tipo,
        itemAtivo: definicao?.ativo,
      };
    }),
    atendimento: {
      id: item.atendimentoRef,
      placa: item.placa ?? '-',
      modelo: null,
      saidaEm: null,
      oficina: null,
    },
    noAparelho: true,
    recusada: item.estado === 'RECUSADO',
  };
};

/** Um par rótulo/valor do cartão. Valor ausente é DITO, não escondido. */
const Dado: React.FC<{ rotulo: string; valor?: string | null }> = ({ rotulo, valor }) => (
  <div className="oe-mv-dado">
    <span className="oe-mv-rotulo">{rotulo}</span>
    <span className={`oe-mv-valor${valor ? '' : ' vazio'}`} title={valor ?? undefined}>
      {valor || 'Não informada'}
    </span>
  </div>
);

/**
 * "Minhas visitas" — o histórico do próprio inspetor (25/09/2026).
 *
 * Existe porque, até aqui, ele não tinha onde reler o que registrou: a árvore só oferece as ações
 * e a fila mostra o que ESTE aparelho subiu (e some ao trocar de celular).
 *
 * 🔴 Falha de carga NUNCA vira "nenhuma visita registrada" (at-60). Para quem passou o dia em
 * campo, essa frase se lê como trabalho perdido — a tela mostra o que está no aparelho e diz, em
 * faixa, que não conseguiu consultar o resto.
 */
const MinhasVisitas: React.FC<Props> = ({ fila, catalogo, onAbrirVeiculo, sidebarRef }) => {
  const [doServidor, setDoServidor] = useState<MinhaVisita[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [filtros, setFiltros] = useState<FiltrosDeMinhasVisitas>({});
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const resposta = await fetchMinhasVisitas({ page: pagina, limit: POR_PAGINA, ...filtros });
      setDoServidor(resposta.itens);
      setTotal(resposta.total);
      setErro(null);
    } catch (error) {
      setDoServidor([]);
      setTotal(0);
      setErro(
        'Não foi possível carregar o seu histórico agora. O que aparece abaixo é o que ainda ' +
          'está guardado neste aparelho — pode haver visitas já enviadas que não estão sendo ' +
          'exibidas.',
      );
      notify.errorFrom(
        error,
        'Não foi possível carregar o seu histórico de visitas. Tente novamente; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, [pagina, filtros]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /**
   * As visitas ainda na fila deste aparelho.
   *
   * ⚠️ Elas só entram na PRIMEIRA página e quando o recorte não depende do servidor: a fila não
   * conhece região, oficina nem a situação do veículo, e filtrá-las por um critério que só o
   * servidor sabe avaliar faria algumas sumirem sem que o inspetor entendesse por quê.
   */
  const locais = useMemo(() => {
    if (pagina > 1) return [];
    if (filtros.regiaoId || filtros.oficinaId || filtros.situacao) return [];
    return fila
      .filter((i) => i.tipo === 'VISITA' && i.estado !== 'ENVIADO')
      .filter((i) => !filtros.placa || (i.placa ?? '').includes(filtros.placa))
      .map((i) => daFila(i, catalogo))
      .filter((v) => !filtros.de || v.dataVisita.slice(0, 10) >= filtros.de!)
      .filter((v) => !filtros.ate || v.dataVisita.slice(0, 10) <= filtros.ate!);
  }, [fila, catalogo, pagina, filtros]);

  const itens: VisitaNaLista[] = useMemo(() => [...locais, ...doServidor], [locais, doServidor]);
  const temFiltro = Object.values(filtros).some(Boolean);

  return (
    <div>
      <p className="oe-hint">
        As visitas que <strong>você</strong> registrou, da mais recente para a mais antiga. A visita
        é <strong>imutável</strong>: para corrigir alguma informação, registre uma visita nova — as
        duas ficam no histórico do veículo.
      </p>

      {/* O contador e o botão de filtros. Não há resumo dos filtros aplicados na tela (padrão do
          sistema): quem quiser conferir ou desfazer reabre o modal, que sempre mostra o que está
          valendo. */}
      <div className="oe-mv-barra">
        <p className="oe-mv-contador">
          {carregando
            ? 'Consultando...'
            : erro
              ? 'Não foi possível contar as suas visitas'
              : `${plural(total, 'visita registrada', 'visitas registradas')}${
                  temFiltro ? ' no recorte escolhido' : ''
                }${locais.length > 0 ? ` · ${locais.length} ainda neste aparelho` : ''}`}
        </p>
        <button
          type="button"
          className={`admin-btn ${temFiltro ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
          onClick={() => setFiltrosAbertos(true)}
        >
          {temFiltro ? 'Filtros aplicados' : 'Filtros'}
        </button>
      </div>

      {erro && (
        <div className="erro-de-tela" role="alert">
          {erro}
        </div>
      )}

      {carregando && <p className="oe-vazio centrado">Carregando o seu histórico...</p>}

      {/* 🔴 O estado vazio só é afirmado quando a consulta RESPONDEU. Com erro, a lista pode estar
          incompleta, e dizer "nenhuma visita" seria afirmar que o trabalho não existe. */}
      {!carregando && !erro && itens.length === 0 && (
        <p className="oe-vazio centrado">
          {temFiltro
            ? 'Nenhuma visita sua no recorte escolhido. Ajuste os filtros para ver as demais.'
            : 'Você ainda não registrou nenhuma visita.'}
        </p>
      )}

      {!carregando && itens.length > 0 && (
        <>
          <div className="oe-mv-lista">
            {itens.map((visita) => (
              <article className="oe-mv-card" key={visita.id}>
                <div className="oe-mv-topo">
                  <span className="oe-mv-placa">{visita.atendimento.placa}</span>
                  {visita.atendimento.saidaEm && (
                    <span className="cilia-badge badge-cinza">
                      Saiu em {dataCurta(visita.atendimento.saidaEm)}
                    </span>
                  )}
                  {visita.noAparelho && (
                    <span
                      className={`cilia-badge ${visita.recusada ? 'badge-vermelho' : 'badge-laranja'}`}
                      title={
                        visita.recusada
                          ? 'O envio foi recusado. Abra a fila para ver o motivo — a visita não se perdeu.'
                          : 'Gravada neste aparelho e ainda não enviada. Ela sobe quando houver rede.'
                      }
                    >
                      {visita.recusada ? 'Envio recusado' : 'No aparelho'}
                    </span>
                  )}
                  <span className="oe-mv-quando">{dataCurta(visita.dataVisita)}</span>
                </div>

                <p className="oe-mv-sub">
                  {visita.atendimento.modelo && <span>{visita.atendimento.modelo}</span>}
                  {visita.atendimento.oficina && (
                    <span>
                      {visita.atendimento.oficina.nome}
                      {visita.atendimento.oficina.regiao
                        ? ` · ${visita.atendimento.oficina.regiao.nome}`
                        : ''}
                    </span>
                  )}
                  {/* As duas datas só aparecem juntas quando DIVERGEM: sem rede, o registro chega
                      ao servidor dias depois da visita. */}
                  {!visita.noAparelho &&
                    dataCurta(visita.dataVisita) !== dataCurta(visita.registradaEm) && (
                      <span
                        className="oe-mv-registro"
                        title="A visita foi feita nesta data e o registro chegou ao sistema depois — você estava sem rede."
                      >
                        enviada em {dataHora(visita.registradaEm)}
                      </span>
                    )}
                </p>

                <div className="oe-mv-dados">
                  <Dado rotulo="Etapa informada" valor={visita.etapa?.nome} />
                  <Dado
                    rotulo="Previsão da etapa"
                    valor={visita.previsaoEtapa ? dataCurta(visita.previsaoEtapa) : null}
                  />
                  <Dado
                    rotulo="Entrega total"
                    valor={visita.previsaoEntrega ? dataCurta(visita.previsaoEntrega) : null}
                  />
                </div>

                {visita.observacao && (
                  <div className="oe-mv-obs">
                    <span className="oe-mv-rotulo">Observação</span>
                    <p>{visita.observacao}</p>
                  </div>
                )}

                {visita.respostas.length > 0 && (
                  <ul className="oe-mv-respostas">
                    {visita.respostas.map((r) => (
                      <li key={r.itemId}>
                        <span className="oe-mv-resposta-rotulo" title={r.rotulo ?? undefined}>
                          {r.rotulo ?? 'Pergunta removida'}
                          {r.itemAtivo === false && (
                            <span className="oe-resposta-inativo"> (item desativado)</span>
                          )}
                        </span>
                        <span className="oe-mv-resposta-valor">{valorLegivel(r)}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="oe-mv-rodape">
                  <span className="oe-mv-fotos">
                    {/* at-132: a contagem soma fotos e vídeos (moram na mesma lista). */}
                    {visita.totalFotos > 0
                      ? plural(visita.totalFotos, 'foto ou vídeo', 'fotos e vídeos')
                      : visita.noAparelho
                        ? 'Ainda neste aparelho'
                        : 'Sem fotos ou vídeos'}
                  </span>
                  {!visita.noAparelho && (
                    <button
                      type="button"
                      className="admin-btn admin-btn-secondary"
                      onClick={() => onAbrirVeiculo(visita.atendimento.id, visita.atendimento.placa)}
                    >
                      Ver veículo
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>

          {total > POR_PAGINA && (
            <Paginacao
              paginaAtual={pagina}
              totalPaginas={Math.max(1, Math.ceil(total / POR_PAGINA))}
              onMudarPagina={setPagina}
            />
          )}
        </>
      )}

      {/* Montagem condicional: é ela que faz o `useState` inicial do modal ser a única fonte do
          rascunho, e o "Cancelar" descartar de verdade (at-61). */}
      {filtrosAbertos && (
        <FiltroMinhasVisitasModal
          aplicados={filtros}
          catalogo={catalogo}
          sidebarRef={sidebarRef}
          onFechar={() => setFiltrosAbertos(false)}
          onAplicar={(novos) => {
            setFiltros(novos);
            // Aplicar volta para a página 1: senão a página 3 do recorte anterior vira lista
            // vazia sem explicação.
            setPagina(1);
            setFiltrosAbertos(false);
          }}
        />
      )}
    </div>
  );
};

export default MinhasVisitas;
